import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Tag, Button, Space, Spin, Alert } from 'antd';
import { EnvironmentOutlined, ReloadOutlined, ShopOutlined, CarOutlined } from '@ant-design/icons';
import RealTimeLocationMap from '../components/RealTimeLocationMap';
import api from '../services/api';

const { Title } = Typography;

const LiveTracking = () => {
  const [loading, setLoading] = useState(false);
  const [activeDeliveries, setActiveDeliveries] = useState([]);
  const [activeProviders, setActiveProviders] = useState([]);
  const [error, setError] = useState(null);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [selectedDeliveryPersonId, setSelectedDeliveryPersonId] = useState(null);

  const fetchLiveTracking = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/live-tracking');
      const data = response.data;

      if (data.success) {
        setActiveDeliveries(data.data.active_deliveries || []);
        setActiveProviders(data.data.active_providers || []);
      } else {
        setError(data.message || 'Failed to load live tracking');
      }
    } catch (err) {
      setError('Failed to load live tracking');
      console.error('Error fetching live tracking:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveTracking();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchLiveTracking, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <Title level={2}>
          <EnvironmentOutlined style={{ marginRight: '8px' }} />
          Live Tracking
        </Title>
        <Button icon={<ReloadOutlined />} onClick={fetchLiveTracking} loading={loading}>
          Refresh
        </Button>
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={12} sm={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <ShopOutlined style={{ fontSize: 24, color: '#1890ff' }} />
              <div style={{ fontSize: 24, fontWeight: 'bold' }}>{activeProviders.length}</div>
              <div style={{ color: '#666' }}>Active Providers</div>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <CarOutlined style={{ fontSize: 24, color: '#52c41a' }} />
              <div style={{ fontSize: 24, fontWeight: 'bold' }}>{activeDeliveries.length}</div>
              <div style={{ color: '#666' }}>Active Deliveries</div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Live Map View" style={{ height: '500px' }}>
            <RealTimeLocationMap
              selectedProviderId={selectedProviderId}
              selectedDeliveryPersonId={selectedDeliveryPersonId}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Active Providers" style={{ height: '240px', overflow: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="small" />
              </div>
            ) : activeProviders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No active providers online
              </div>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }}>
                {activeProviders.map((provider) => (
                  <Card
                    key={provider.id}
                    size="small"
                    style={{
                      marginBottom: '8px',
                      cursor: 'pointer',
                      borderColor: selectedProviderId === provider.id ? '#1890ff' : undefined,
                    }}
                    onClick={() => {
                      setSelectedProviderId(provider.id);
                      setSelectedDeliveryPersonId(null);
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 'bold' }}>
                          {provider.name || provider.full_name}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {provider.phone_number || provider.phone || 'No phone'}
                        </div>
                      </div>
                      <Tag color="green">Online</Tag>
                    </div>
                  </Card>
                ))}
              </Space>
            )}
          </Card>

          <Card
            title="Active Deliveries"
            style={{ height: '240px', overflow: 'auto', marginTop: '16px' }}
          >
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="small" />
              </div>
            ) : activeDeliveries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No active deliveries
              </div>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }}>
                {activeDeliveries.map((delivery) => (
                  <Card
                    key={delivery.id}
                    size="small"
                    style={{
                      marginBottom: '8px',
                      cursor: 'pointer',
                      borderColor:
                        selectedDeliveryPersonId === delivery.delivery_person?.id
                          ? '#52c41a'
                          : undefined,
                    }}
                    onClick={() => {
                      const dpId = delivery.delivery_person?.id;
                      if (dpId) {
                        setSelectedDeliveryPersonId(dpId);
                        setSelectedProviderId(null);
                      }
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 'bold' }}>#{delivery.id}</div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {delivery.delivery_person?.name ||
                            delivery.delivery_person?.full_name ||
                            'Unassigned'}
                        </div>
                      </div>
                      <Tag color="orange">{delivery.order_status}</Tag>
                    </div>
                  </Card>
                ))}
              </Space>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default LiveTracking;
